import { useQuery } from "@apollo/client";
import { useCallback, useMemo, useRef } from "react";

import { SearchQueryOrderDirection } from "types/generated";

import { SEARCH_SPACELIFT_VCS_REPO_REVISIONS } from "./gql";

type UseSearchSpaceliftVcsRepoRevisionsProps = {
  repoId: string;
  fullTextSearch?: string;
  skip?: boolean;
};

const PAGE_SIZE = 50;
const ORDER_BY = [{ field: "createdAt", direction: SearchQueryOrderDirection.Desc }];

const useSearchSpaceliftVcsRepoRevisions = ({
  repoId,
  fullTextSearch,
  skip,
}: UseSearchSpaceliftVcsRepoRevisionsProps) => {
  const fetchingRef = useRef(false);

  const { data, loading, error, previousData, fetchMore } = useQuery(
    SEARCH_SPACELIFT_VCS_REPO_REVISIONS,
    {
      variables: {
        repoID: repoId,
        input: {
          after: null,
          first: PAGE_SIZE,
          fullTextSearch: fullTextSearch || null,
          orderBy: ORDER_BY,
          predicates: null,
          requestedPage: null,
        },
      },
      skip,
    }
  );

  const revisions = useMemo(() => {
    const edges = data?.searchRevisions.edges || previousData?.searchRevisions.edges;
    return edges?.map((edge) => edge.node) || [];
  }, [data, previousData]);

  const pageInfo = data?.searchRevisions.pageInfo || previousData?.searchRevisions.pageInfo;
  const hasMore = !!pageInfo?.hasNextPage;

  const loadMore = useCallback(() => {
    if (fetchingRef.current || !hasMore) return;

    fetchingRef.current = true;

    fetchMore({
      variables: {
        input: {
          after: pageInfo?.endCursor,
          first: PAGE_SIZE,
          fullTextSearch: fullTextSearch || null,
          orderBy: ORDER_BY,
          predicates: null,
          requestedPage: null,
        },
      },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous;

        return {
          searchRevisions: {
            ...fetchMoreResult.searchRevisions,
            edges: [
              ...previous.searchRevisions.edges,
              ...fetchMoreResult.searchRevisions.edges,
            ],
          },
        };
      },
    }).finally(() => {
      fetchingRef.current = false;
    });
  }, [fetchMore, fullTextSearch, hasMore, pageInfo?.endCursor]);

  return {
    revisions,
    loading,
    error,
    hasMore,
    loadMore,
  };
};

export default useSearchSpaceliftVcsRepoRevisions;
